import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Download, ExternalLink, Plus, Star, X } from "lucide-react";
import { api, type BillingStatus, type LanguageOption } from "../../api";
import { useProfile } from "../../layout/AppShell";
import DangerZone from "../../components/DangerZone";
import LinkCard from "../../components/LinkCard";
import { fadeUp, stagger } from "../../lib/motion";
import { cn } from "../../lib/format";

// The five most-requested languages surface as chips; the remaining
// seven live behind a "…more" affordance so the picker stays scannable
// (12 chips in a flex-wrap turns into a wall of text). If the user's
// current selection is in the "more" bucket we auto-expand so they can
// still see what they picked.
const PRIMARY_LANG_CODES = ["de", "es", "fr", "it", "pt"];
const CEFR_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

// Pace presets — map a single user choice to messages-per-day so the
// "honest tutor" promise stays a one-tap decision rather than a slider.
const PACE_OPTIONS = [
  { id: "light",    label: "Light",    n: 2,  sub: "~2 messages/day" },
  { id: "steady",   label: "Steady",   n: 5,  sub: "~5 messages/day" },
  { id: "constant", label: "Constant", n: 12, sub: "~12 messages/day" },
] as const;

type PaceId = typeof PACE_OPTIONS[number]["id"];

function paceFromTarget(n: number): PaceId {
  // Closest preset by target count; default Steady.
  let best: PaceId = "steady";
  let bestD = Infinity;
  for (const p of PACE_OPTIONS) {
    const d = Math.abs(p.n - n);
    if (d < bestD) {
      best = p.id;
      bestD = d;
    }
  }
  return best;
}

function fmtTime(hour: number): string {
  const h = String(Math.max(0, Math.min(23, Math.round(hour)))).padStart(2, "0");
  return `${h}:00`;
}
function parseHour(s: string): number {
  const m = /^(\d{1,2}):/.exec(s);
  if (!m) return 0;
  return Math.max(0, Math.min(23, parseInt(m[1], 10)));
}

const PERSONA_LABEL: Record<string, string> = {
  casual: "Casual",
  grumpy: "Grumpy Old Timer",
  optimist: "Cheerful Optimist",
  philosopher: "Deep Thinker",
  gossip: "Nosy Friend",
  minimalist: "Dry Minimalist",
};

export default function Profile() {
  const { profile, setProfile } = useProfile();
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [savingPace, setSavingPace] = useState(false);
  const [savingQuiet, setSavingQuiet] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Language catalog powers the target-language + native-language +
  // multi-language pickers. Load once — the registry rarely changes.
  const [catalog, setCatalog] = useState<LanguageOption[]>([]);
  const [nativeOptions, setNativeOptions] = useState<LanguageOption[]>([]);

  // Name is staged on input, saved on blur or explicit Save — matches
  // the Pace pattern (no accidental writes on every keystroke).
  const [nameInput, setNameInput] = useState(profile.first_name ?? "");
  const [savingName, setSavingName] = useState(false);
  const [savingNative, setSavingNative] = useState<string | null>(null);
  const [savingActive, setSavingActive] = useState<string | null>(null);
  const [savingCefr, setSavingCefr] = useState<string | null>(null);

  // Language add/remove for paid tiers (owned > 1). Kept scoped to
  // this page so the LanguagesPanel component can stay dead until we
  // add multi-lang UX elsewhere.
  const [addCode, setAddCode] = useState("");
  const [langBusy, setLangBusy] = useState<string | null>(null);

  // Honest-section toggles persist via PATCH /api/me. "Streaks" stays
  // off forever — the brand promise is "no streaks" and the toggle
  // exists only to surface the absence honestly.
  const voiceOn = profile.voice_enabled ?? true;
  const emailWeekly = profile.email_weekly ?? true;
  const [togglingVoice, setTogglingVoice] = useState(false);
  const [togglingEmail, setTogglingEmail] = useState(false);

  useEffect(() => {
    api.getBillingStatus().then(setBilling).catch(() => {});
    api.getLanguages().then((r) => {
      if (r.languages?.length) setCatalog(r.languages);
      if (r.native_options?.length) setNativeOptions(r.native_options);
    }).catch(() => {});
  }, []);

  // Keep the name input in sync when the profile hydrates or updates
  // from another tab. Only reset when the source-of-truth value moves
  // AND the user isn't mid-edit.
  useEffect(() => {
    if (!savingName) setNameInput(profile.first_name ?? "");
  }, [profile.first_name, savingName]);

  const tzName = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "your local time"; }
  }, []);

  const currentPersona = profile.persona || "casual";
  const savedTarget = profile.daily_message_target ?? 5;
  const savedPace = paceFromTarget(savedTarget);
  // Pace is staged: a click selects, the user then explicitly confirms.
  // This avoids the "tapped a tile to read it, accidentally re-set my cadence" bug.
  const [pendingPace, setPendingPace] = useState<PaceId | null>(null);
  const stagedPace = pendingPace ?? savedPace;
  const startTime = fmtTime(profile.quiet_end_hour ?? 8);
  const endTime = fmtTime(profile.quiet_start_hour ?? 22);

  async function commitPace() {
    if (!pendingPace || pendingPace === savedPace || savingPace) return;
    const opt = PACE_OPTIONS.find((x) => x.id === pendingPace);
    if (!opt) return;
    setSavingPace(true);
    setErr(null);
    try {
      const updated = await api.updateMe({ daily_message_target: opt.n });
      setProfile(updated);
      setPendingPace(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "couldn't save pace");
    } finally {
      setSavingPace(false);
    }
  }

  async function setQuietHours(start: string, end: string) {
    if (savingQuiet) return;
    setSavingQuiet(true);
    setErr(null);
    try {
      const updated = await api.updateMe({
        quiet_end_hour: parseHour(start),
        quiet_start_hour: parseHour(end),
      });
      setProfile(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "couldn't save quiet hours");
    } finally {
      setSavingQuiet(false);
    }
  }

  async function onExport() {
    if (exportBusy) return;
    setExportBusy(true);
    try {
      // Server-side export endpoint TBD — fall back to a client-side
      // CSV from the words list so the button does something useful.
      const r = await api.getWords("seen", profile.active_language);
      const rows = [
        ["word", "gloss", "pos", "gender", "level", "mastery", "reps"],
        ...r.words.map((w) => [w.word, w.gloss, w.pos, w.gender, w.level, w.mastery, w.reps]),
      ];
      const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ilmli-vocabulary-${profile.active_language}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "export failed");
    } finally {
      setExportBusy(false);
    }
  }

  async function toggleVoice() {
    if (togglingVoice) return;
    setTogglingVoice(true);
    setErr(null);
    try {
      const updated = await api.updateMe({ voice_enabled: !voiceOn });
      setProfile(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "couldn't toggle voice notes");
    } finally {
      setTogglingVoice(false);
    }
  }
  async function saveName() {
    const next = nameInput.trim();
    if (!next || next === (profile.first_name ?? "") || savingName) return;
    setSavingName(true);
    setErr(null);
    try {
      const updated = await api.updateMe({ first_name: next });
      setProfile(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "couldn't save name");
    } finally {
      setSavingName(false);
    }
  }

  async function setNativeLang(code: string) {
    if (code === profile.native_language_code || savingNative) return;
    setSavingNative(code);
    setErr(null);
    try {
      const updated = await api.updateMe({ native_language_code: code });
      setProfile(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "couldn't switch native language");
    } finally {
      setSavingNative(null);
    }
  }

  async function setActiveLang(code: string) {
    if (code === profile.active_language || savingActive) return;
    setSavingActive(code);
    setErr(null);
    try {
      const updated = await api.updateMe({ active_language: code });
      setProfile(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "couldn't switch active language");
    } finally {
      setSavingActive(null);
    }
  }

  async function setCefrLevel(level: string) {
    if (level === profile.cefr_level || savingCefr) return;
    setSavingCefr(level);
    setErr(null);
    try {
      const updated = await api.updateMe({ cefr_level: level });
      setProfile(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "couldn't save level");
    } finally {
      setSavingCefr(null);
    }
  }

  async function addOwnedLanguage() {
    if (!addCode || langBusy) return;
    setLangBusy(addCode);
    setErr(null);
    try {
      const updated = await api.addLanguage(addCode);
      setProfile(updated);
      setAddCode("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "couldn't add language");
    } finally {
      setLangBusy(null);
    }
  }

  async function removeOwnedLanguage(code: string) {
    if (langBusy) return;
    setLangBusy(code);
    setErr(null);
    try {
      const updated = await api.removeLanguage(code);
      setProfile(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "couldn't remove language");
    } finally {
      setLangBusy(null);
    }
  }

  async function toggleEmail() {
    if (togglingEmail) return;
    setTogglingEmail(true);
    setErr(null);
    try {
      const updated = await api.updateMe({ email_weekly: !emailWeekly });
      setProfile(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "couldn't toggle email weekly");
    } finally {
      setTogglingEmail(false);
    }
  }

  return (
    <motion.div initial="hidden" animate="show" variants={stagger(0.05)}>
      <motion.div variants={fadeUp}>
        <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-faint">
          account · preferences
        </div>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">
          A few <span className="italic text-terracotta">knobs.</span>
        </h1>
        <p className="mt-3 max-w-xl font-serif italic text-ink-soft text-base">
          The fewer the better. We don't believe in 47-screen settings — if it's important,
          it should live here.
        </p>
      </motion.div>

      {err && <div className="mt-6 text-terracotta text-sm">{err}</div>}

      <div className="mt-12 space-y-12">
        {/* YOU — name + native language. Sits at the top because
            native language was the single biggest reported gap: the
            server defaults to English and there was no UI to change it. */}
        <Section
          title="You"
          sub="Your name and your native language. Glosses appear in your native."
        >
          <div className="space-y-6">
            <div>
              <div className="font-mono text-[11px] tracking-wide uppercase text-ink-faint mb-2">Your name</div>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); (e.currentTarget as HTMLInputElement).blur(); } }}
                  placeholder="your name"
                  aria-label="Your name"
                  className="font-serif text-[18px] bg-transparent border-b-2 border-ink focus:outline-none focus:border-terracotta px-1 py-1 min-w-[180px] w-[240px] placeholder:text-ink-faint/60"
                />
                {savingName && (
                  <span className="font-mono text-[11px] text-ink-faint">saving…</span>
                )}
                {!savingName && nameInput.trim() && nameInput.trim() !== (profile.first_name ?? "") && (
                  <span className="font-mono text-[11px] text-ink-faint">
                    press <kbd className="font-mono">Enter</kbd> to save
                  </span>
                )}
              </div>
            </div>

            <div>
              <div className="font-mono text-[11px] tracking-wide uppercase text-ink-faint mb-2">Your native language</div>
              <LanguageChipRow
                catalog={nativeOptions.length ? nativeOptions : catalog}
                selected={profile.native_language_code || "en"}
                onPick={setNativeLang}
                busyCode={savingNative}
              />
            </div>
          </div>
        </Section>

        {/* LANGUAGE — target language + level + multi-language management. */}
        <Section
          title="Language"
          sub="What you're learning and where you are. Level auto-calibrates over time."
        >
          <div className="space-y-6">
            <div>
              <div className="font-mono text-[11px] tracking-wide uppercase text-ink-faint mb-2">Currently learning</div>
              <LanguageChipRow
                catalog={catalog}
                selected={profile.active_language}
                onPick={setActiveLang}
                busyCode={savingActive}
              />
            </div>

            <div>
              <div className="font-mono text-[11px] tracking-wide uppercase text-ink-faint mb-2">Your level</div>
              <div className="flex flex-wrap gap-2">
                {CEFR_LEVELS.map((lv) => {
                  const active = lv === profile.cefr_level;
                  const busy = savingCefr === lv;
                  return (
                    <button
                      key={lv}
                      type="button"
                      onClick={() => setCefrLevel(lv)}
                      disabled={!!savingCefr}
                      className={cn(
                        "px-3 py-1 rounded-full border font-sans text-[13px] tabular-nums transition-colors min-w-[44px]",
                        active
                          ? "border-terracotta bg-terracotta text-paper"
                          : "border-[rgba(42,36,26,0.18)] text-ink-soft hover:border-terracotta hover:text-terracotta",
                        busy && "opacity-60",
                      )}
                    >
                      {lv}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Multi-language management. Only shown when the tier
                actually allows more than one — otherwise the panel is
                confusing (add button disabled with no useful message). */}
            {billing && billing.languages_limit > 1 && (
              <div>
                <div className="font-mono text-[11px] tracking-wide uppercase text-ink-faint mb-2">
                  Your languages · <span className="text-ink-soft">{(profile.languages ?? [profile.active_language]).length} of {billing.languages_limit >= 999 ? "unlimited" : billing.languages_limit}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(profile.languages ?? [profile.active_language]).map((code) => {
                    const info = catalog.find((l) => l.code === code);
                    const isActive = code === profile.active_language;
                    const isBusy = langBusy === code;
                    const owned = profile.languages ?? [profile.active_language];
                    return (
                      <span
                        key={code}
                        className={cn(
                          "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[13px]",
                          isActive
                            ? "border-terracotta bg-terracotta/10 text-ink"
                            : "border-[rgba(42,36,26,0.18)] text-ink-soft",
                          isBusy && "opacity-60",
                        )}
                      >
                        {isActive && <Star size={12} className="text-terracotta" fill="currentColor" />}
                        {info?.name ?? code}
                        {owned.length > 1 && !isActive && (
                          <button
                            type="button"
                            onClick={() => removeOwnedLanguage(code)}
                            disabled={!!langBusy}
                            className="ml-1 text-ink-faint hover:text-terracotta"
                            aria-label={`Remove ${info?.name ?? code}`}
                          >
                            <X size={12} />
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
                {(() => {
                  const owned = profile.languages ?? [profile.active_language];
                  const available = catalog.filter((l) => !owned.includes(l.code));
                  const canAdd = owned.length < billing.languages_limit;
                  if (!canAdd || available.length === 0) {
                    return (
                      <div className="mt-3 font-mono text-[11px] text-ink-faint italic">
                        {!canAdd
                          ? `tier limit reached (${billing.languages_limit})`
                          : "all languages added"}
                      </div>
                    );
                  }
                  return (
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <select
                        value={addCode}
                        onChange={(e) => setAddCode(e.target.value)}
                        className="font-sans text-[13px] px-2.5 py-1 rounded-md border border-[rgba(42,36,26,0.18)] bg-paper focus:outline-none focus:border-terracotta"
                      >
                        <option value="">— pick a language to add —</option>
                        {available.map((l) => (
                          <option key={l.code} value={l.code}>{l.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={addOwnedLanguage}
                        disabled={!addCode || !!langBusy}
                        className="inline-flex items-center gap-1 rounded-md bg-terracotta text-paper px-2.5 py-1 text-[12px] hover:bg-terracotta-soft disabled:opacity-60"
                      >
                        <Plus size={12} /> Add
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </Section>

        {/* TUTOR */}
        <Section
          title="Tutor"
          sub="The one you'll hear from. You can switch any time — they share the same memory of you."
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Object.entries(PERSONA_LABEL).slice(0, 3).map(([id, label]) => {
              const active = id === currentPersona;
              return (
                <Link
                  key={id}
                  to="/app/personas"
                  className={cn(
                    "rounded-md border px-4 py-4 text-left transition-colors",
                    active
                      ? "border-ink bg-ink text-paper"
                      : "border-[rgba(42,36,26,0.18)] bg-paper hover:border-terracotta",
                  )}
                >
                  <div className="font-serif text-xl leading-tight">{label}</div>
                  <div
                    className={cn(
                      "font-mono text-[10px] tracking-wide uppercase mt-2",
                      active ? "text-paper/55" : "text-ink-faint",
                    )}
                  >
                    {active ? "current" : "available"}
                  </div>
                </Link>
              );
            })}
          </div>
          <Link
            to="/app/personas"
            className="mt-4 inline-block text-[13px] text-terracotta underline decoration-terracotta/40 hover:decoration-terracotta"
          >
            See all six personas →
          </Link>
          <Link
            to="/app/personas/new"
            className="mt-2 ml-5 inline-block text-[13px] text-terracotta underline decoration-terracotta/40 hover:decoration-terracotta"
          >
            Or design your own →
          </Link>
        </Section>

        {/* PACE */}
        <Section
          title="Pace"
          sub="How often I'll text you. You can override per day with quiet hours below."
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PACE_OPTIONS.map((p) => {
              const isSaved = p.id === savedPace;
              const isStaged = p.id === stagedPace;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() =>
                    setPendingPace(p.id === savedPace ? null : p.id)
                  }
                  disabled={savingPace}
                  className={cn(
                    "rounded-md border px-4 py-4 text-left transition-all relative",
                    isStaged && !isSaved
                      ? "border-terracotta border-2 bg-paper"
                      : isSaved
                        ? "border-terracotta bg-terracotta-tint border-2"
                        : "border-[rgba(42,36,26,0.18)] bg-paper hover:border-terracotta",
                  )}
                >
                  {isSaved && (
                    <span className="absolute top-2 right-2 font-mono text-[9px] tracking-wide uppercase text-terracotta">
                      current
                    </span>
                  )}
                  <div
                    className={cn(
                      "font-serif text-xl leading-tight",
                      (isSaved || isStaged) && "text-terracotta",
                    )}
                  >
                    {p.label}
                  </div>
                  <div className="font-mono text-[11px] text-ink-faint mt-2">{p.sub}</div>
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <span className="font-mono text-[11px] text-ink-faint">
              tutor target now: <strong className="text-ink-soft">{savedTarget} msgs/day</strong>
            </span>
            {pendingPace && pendingPace !== savedPace && (
              <>
                <button
                  onClick={commitPace}
                  disabled={savingPace}
                  className="rounded-md bg-terracotta text-paper px-3 py-1 text-[12px] hover:bg-terracotta-soft disabled:opacity-60"
                >
                  {savingPace ? "saving…" : `Save — switch to ${PACE_OPTIONS.find((p) => p.id === pendingPace)?.label}`}
                </button>
                <button
                  onClick={() => setPendingPace(null)}
                  className="font-mono text-[11px] text-ink-soft underline decoration-ink-faint/40 hover:decoration-terracotta"
                >
                  cancel
                </button>
              </>
            )}
          </div>
        </Section>

        {/* QUIET HOURS */}
        <Section
          title="Quiet hours"
          sub="When I won't message you. Your local time."
        >
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-serif italic text-ink-soft">from</span>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setQuietHours(startTime, e.target.value)}
              className="font-mono text-[16px] px-3 py-1.5 rounded-md border border-[rgba(42,36,26,0.18)] bg-paper focus:outline-none focus:border-terracotta"
              aria-label="Quiet starts"
            />
            <span className="font-serif italic text-ink-soft">to</span>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setQuietHours(e.target.value, endTime)}
              className="font-mono text-[16px] px-3 py-1.5 rounded-md border border-[rgba(42,36,26,0.18)] bg-paper focus:outline-none focus:border-terracotta"
              aria-label="Quiet ends"
            />
            <span className="font-serif italic text-ink-faint text-[14px]">· {tzName}</span>
          </div>
        </Section>

        {/* THE HONEST SECTION */}
        <Section
          title="The honest section"
          sub="Things on by default. Turn them off if they don't suit you."
        >
          <div className="space-y-5">
            <ToggleRow
              label="Voice notes"
              sub="My TTS replies + your transcribed memos. Off = text only."
              on={voiceOn}
              onChange={toggleVoice}
              busy={togglingVoice}
            />
            <ToggleRow
              label="Email me weekly"
              sub="A short note Sunday evenings — what we covered, no marketing."
              on={emailWeekly}
              onChange={toggleEmail}
              busy={togglingEmail}
            />
            <ToggleRow
              label="Streaks"
              sub="Off by default. We don't recommend turning this on."
              on={false}
              onChange={() => { /* sealed off — see comment in <ToggleRow /> */ }}
              disabled
              note="(unavailable on purpose)"
            />
          </div>
        </Section>

        {/* ACCOUNT */}
        <Section title="Account" sub={null}>
          <div className="divide-y divide-[rgba(42,36,26,0.10)] border-y border-[rgba(42,36,26,0.10)]">
            <Row
              left={
                <>
                  <div className="font-serif text-[16px]">{profile.email}</div>
                  <div className="font-mono text-[11px] text-ink-faint mt-1">
                    {profile.activated ? "Telegram connected" : "Telegram not linked"}
                  </div>
                </>
              }
              right={
                <a
                  href="mailto:hello@ilmli.ai"
                  className="font-mono text-[12px] text-ink-soft underline decoration-ink-faint/40 hover:decoration-terracotta"
                >
                  Change email
                </a>
              }
            />
            {/* Connect-Telegram widget when this account isn't currently
                linked. Covers two flows: (1) brand-new account that hasn't
                yet linked, (2) account whose Telegram was moved to a
                different ilmli account — they can reclaim it by minting
                a code here and sending `/start <code>` from Telegram. */}
            {!profile.activated && (
              <div className="py-5">
                <div className="font-serif text-[16px]">Connect Telegram</div>
                <div className="font-mono text-[11px] text-ink-faint mt-1 mb-4">
                  Attach this Telegram chat to this account. If your Telegram is currently linked to another ilmli account, this moves it here.
                </div>
                <LinkCard langCode={profile.active_language} />
              </div>
            )}
            <Row
              left={
                <>
                  <div className="font-serif text-[16px]">
                    {billing?.tier_name ?? (billing?.tier ? billing.tier : "Loading…")}
                  </div>
                  <div className="font-mono text-[11px] text-ink-faint mt-1">
                    {billing && billing.voice_limit > 0
                      ? `Voice: ${billing.voice_used} of ${billing.voice_limit} used`
                      : billing
                        ? "Free trial"
                        : ""}
                  </div>
                </>
              }
              right={
                billing?.has_stripe_customer ? (
                  <button
                    onClick={async () => {
                      try {
                        const { url } = await api.createPortal();
                        window.location.href = url;
                      } catch (e) {
                        setErr(e instanceof Error ? e.message : "portal failed");
                      }
                    }}
                    className="inline-flex items-center gap-1.5 font-mono text-[12px] text-ink-soft underline decoration-ink-faint/40 hover:decoration-terracotta"
                  >
                    Manage plan <ExternalLink size={11} />
                  </button>
                ) : (
                  <span className="font-mono text-[11px] text-ink-faint italic">free trial</span>
                )
              }
            />
            <Row
              left={
                <>
                  <div className="font-serif text-[16px]">Export your vocabulary</div>
                  <div className="font-mono text-[11px] text-ink-faint mt-1">
                    CSV. Yours forever, Anki-friendly.
                  </div>
                </>
              }
              right={
                <button
                  onClick={onExport}
                  disabled={exportBusy}
                  className="inline-flex items-center gap-1.5 rounded-md border border-[rgba(42,36,26,0.20)] px-3 py-1.5 font-mono text-[12px] text-ink-soft hover:border-terracotta hover:text-terracotta disabled:opacity-60"
                >
                  <Download size={12} />
                  {exportBusy ? "exporting…" : "Download .csv"}
                </button>
              }
            />
            {/* Pause "for a while" needs a backend endpoint that doesn't
                exist yet (only `paused` boolean, admin-only). Hidden until
                the API gains a user-facing pause action. */}
            {/* Trigger row + the expanded confirmation block live in the same
                <details>, but the popover is its own block (not stuffed into
                Row's right slot) so it can occupy full row width on mobile
                without squishing the label column. */}
            <details className="group py-4">
              <summary className="flex items-start gap-4 cursor-pointer list-none">
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-[16px] text-terracotta">Delete account</div>
                  <div className="font-mono text-[11px] text-ink-faint mt-1">
                    Everything goes. Including the words you learned. Plain English warning, no dark patterns.
                  </div>
                </div>
                <div className="shrink-0 font-mono text-[12px] text-terracotta underline decoration-terracotta/40 group-hover:decoration-terracotta">
                  Delete <ArrowRight size={11} className="inline ml-0.5 -mt-0.5" />
                </div>
              </summary>
              <div className="mt-4 md:max-w-md md:ml-auto">
                <DangerZone email={profile.email} />
              </div>
            </details>
          </div>
        </Section>
      </div>
    </motion.div>
  );
}

function Section({
  title,
  sub,
  children,
}: {
  title: string;
  sub: string | null;
  children: React.ReactNode;
}) {
  return (
    <motion.section variants={fadeUp} className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 md:gap-12 items-start">
      <div>
        <h2 className="font-serif text-2xl leading-tight">{title}</h2>
        {sub && (
          <p className="mt-2 font-serif italic text-ink-soft text-[13px] leading-relaxed">
            {sub}
          </p>
        )}
      </div>
      <div>{children}</div>
    </motion.section>
  );
}

function Row({ left, right }: { left: React.ReactNode; right: React.ReactNode }) {
  return (
    <div className="flex items-start gap-4 py-4">
      <div className="flex-1 min-w-0">{left}</div>
      <div className="shrink-0">{right}</div>
    </div>
  );
}

function LanguageChipRow({
  catalog,
  selected,
  onPick,
  busyCode,
}: {
  catalog: LanguageOption[];
  selected: string;
  onPick: (code: string) => void;
  busyCode: string | null;
}) {
  // Primary chips = the intersection of PRIMARY_LANG_CODES and what
  // the catalog actually offers, in the constant's order. Everything
  // else lives in the "…more" bucket. If the current selection is in
  // "more" (e.g. English native, Russian target), auto-expand so the
  // user can see + change it.
  const primary = PRIMARY_LANG_CODES
    .map((c) => catalog.find((l) => l.code === c))
    .filter((l): l is LanguageOption => !!l);
  const rest = catalog.filter(
    (l) => !PRIMARY_LANG_CODES.includes(l.code),
  );
  const selectedInRest = rest.some((l) => l.code === selected);
  const [expanded, setExpanded] = useState(selectedInRest);

  // Keep expanded true if the selection lands in the rest bucket
  // after the fact (e.g. user picks a "more" chip; on next render
  // the row should still show all of them).
  useEffect(() => {
    if (selectedInRest) setExpanded(true);
  }, [selectedInRest]);

  const visible = expanded ? [...primary, ...rest] : primary;

  return (
    <div className="flex flex-wrap gap-2">
      {visible.map((l) => {
        const active = l.code === selected;
        const busy = busyCode === l.code;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => onPick(l.code)}
            disabled={!!busyCode}
            title={l.endonym && l.endonym !== l.name ? l.endonym : undefined}
            className={cn(
              "px-3.5 py-1 rounded-full border font-sans text-[13px] transition-colors",
              active
                ? "border-terracotta bg-terracotta text-paper"
                : "border-[rgba(42,36,26,0.18)] text-ink-soft hover:border-terracotta hover:text-terracotta",
              busy && "opacity-60",
            )}
          >
            {l.name}
          </button>
        );
      })}
      {!expanded && rest.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="px-3 py-1 rounded-full border border-dashed border-[rgba(42,36,26,0.25)] font-sans text-[13px] text-ink-faint hover:border-terracotta hover:text-terracotta transition-colors"
        >
          …more ({rest.length})
        </button>
      )}
    </div>
  );
}

function ToggleRow({
  label,
  sub,
  on,
  onChange,
  disabled,
  busy,
  note,
}: {
  label: string;
  sub: string;
  on: boolean;
  onChange: () => void;
  disabled?: boolean;
  busy?: boolean;
  note?: string;
}) {
  const inactive = disabled || busy;
  return (
    <div className="flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <div className="font-serif text-[16px]">{label}</div>
        <div className="font-mono text-[11px] text-ink-faint mt-1">{sub}</div>
        {note && <div className="font-mono italic text-[11px] text-ink-faint mt-1">{note}</div>}
      </div>
      <button
        type="button"
        onClick={inactive ? undefined : onChange}
        disabled={inactive}
        aria-pressed={on}
        className={cn(
          "shrink-0 relative w-11 h-6 rounded-full transition-colors",
          on ? "bg-terracotta" : "bg-[rgba(42,36,26,0.20)]",
          disabled && "opacity-50 cursor-not-allowed",
          busy && "opacity-70",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 w-5 h-5 rounded-full bg-paper shadow transition-transform",
            on ? "translate-x-[1.375rem]" : "translate-x-0.5",
          )}
        />
      </button>
    </div>
  );
}
